import type { TimeAwareMediaAsset } from '@lumen5/luminary';

import type { BoundingBox } from 'js/src/client';
import type { InterleavedWord } from 'js/src/agent/tools/transcript-tokens';
import { interleavedTokenToString } from 'js/src/agent/tools/transcript-tokens';

/**
 * The shot table for a clip: every camera shot inside the part of the recording a clip plays,
 * the transcript words each shot plays under, and the faces found in it. All times are
 * milliseconds into the recording (transcript time), so they survive later cuts to the clip.
 */

/** A part of the recording the clip plays, as [startMs, endMs). */
export type KeptRegion = [number, number];

export interface FaceCheck {
    timeMs: number;
    /** Faces ordered left to right, or null when detection failed at this point. */
    faces: BoundingBox[] | null;
}

export interface ClipShot {
    /** 1-based, in playback order. */
    number: number;
    startMs: number;
    endMs: number;
    tokens: InterleavedWord[];
    /** Face detection through the shot; the check nearest the middle is the one the contact sheet shows. */
    faceChecks: FaceCheck[];
    /** The camera angle of a shot with people in it, as a letter shared by every shot framed the same way. */
    angle: string;
    /** B-roll, or a brief flash between other angles: it plays under the base scene's centre crop. */
    isBroll: boolean;
}

const MIN_SAMPLE_STEP_MS = 250;
const MAX_SAMPLES = 200;
const FACE_CHECK_EDGE_MS = 750;
const SHOT_LONG_ENOUGH_TO_MOVE_MS = 6000;
const SHOT_LONG_ENOUGH_FOR_QUARTERS_MS = 30000;
const BRIEF_SHOT_MS = 1000;
const FACE_MOVED_FRACTION = 0.1;
const SAME_ANGLE_CENTRE_FRACTION = 0.06;
const SAME_ANGLE_HEIGHT_FRACTION = 0.05;
const WORD_PREVIEW_EDGE = 6;
const BRIEF_FLASH_MS = 1500;
/** People this many face-widths apart each have room of their own, as in an interview two-shot. */
const FAR_APART_FACE_WIDTHS = 2;

export function keptDurationMs(regions: KeptRegion[]): number {
    return regions.reduce((total, [start, end]) => total + Math.max(0, end - start), 0);
}

/** Evenly spaced sample times across the kept regions, dense enough to catch quick cuts in a short clip. */
export function planClipSampleTimesMs(regions: KeptRegion[]): number[] {
    const stepMs = Math.max(MIN_SAMPLE_STEP_MS, keptDurationMs(regions) / MAX_SAMPLES);
    return regions.flatMap(([start, end]) => {
        const times: number[] = [];
        for (let t = start; t < end; t += stepMs) {
            times.push(t);
        }
        return times;
    });
}

/** How far into the clip a recording time plays, skipping the parts of the recording the clip leaves out. */
export function toClipTimeMs(recordingTimeMs: number, regions: KeptRegion[]): number {
    let clipTime = 0;
    for (const [start, end] of regions) {
        if (recordingTimeMs <= start) {
            break;
        }
        clipTime += Math.min(recordingTimeMs, end) - start;
    }
    return clipTime;
}

/**
 * Split each kept part of the recording at its cuts, and give every shot the words that start in
 * it. Each kept part splits on its own, so no shot runs across a stretch the clip skips.
 */
export function buildClipShots(
    cutTimesMs: number[],
    regions: KeptRegion[],
    tokens: InterleavedWord[],
): ClipShot[] {
    const shots: ClipShot[] = [];
    for (const [start, end] of regions) {
        const boundaries = [start, ...cutTimesMs.filter((cut) => cut > start && cut < end), end];
        for (let i = 0; i < boundaries.length - 1; i++) {
            if (boundaries[i + 1] > boundaries[i]) {
                shots.push({
                    number: shots.length + 1,
                    startMs: boundaries[i],
                    endMs: boundaries[i + 1],
                    tokens: [],
                    faceChecks: [],
                    angle: '',
                    isBroll: false,
                });
            }
        }
    }
    if (shots.length === 0) {
        return shots;
    }
    for (const token of tokens) {
        const owner =
            shots.find((shot) => token.startTime >= shot.startMs && token.startTime < shot.endMs) ??
            [...shots].reverse().find((shot) => shot.startMs <= token.startTime) ??
            shots[0];
        owner.tokens.push(token);
    }
    return shots;
}

/** Where to look for faces in a shot: its middle, plus its ends and quarters once it runs long enough for people to move. */
export function faceCheckTimesMs(shot: Pick<ClipShot, 'startMs' | 'endMs'>): number[] {
    const { startMs, endMs } = shot;
    const duration = endMs - startMs;
    const middle = startMs + duration / 2;
    if (duration >= SHOT_LONG_ENOUGH_FOR_QUARTERS_MS) {
        return [
            startMs + FACE_CHECK_EDGE_MS,
            startMs + duration / 4,
            middle,
            startMs + (3 * duration) / 4,
            endMs - FACE_CHECK_EDGE_MS,
        ];
    }
    if (duration >= SHOT_LONG_ENOUGH_TO_MOVE_MS) {
        return [startMs + FACE_CHECK_EDGE_MS, middle, endMs - FACE_CHECK_EDGE_MS];
    }
    return [middle];
}

/** Faces numbered the way frame_speaker numbers them: by how far across the picture their centre sits. */
export function sortFacesLeftToRight(faces: BoundingBox[]): BoundingBox[] {
    return [...faces].sort((a, b) => a.x + a.width / 2 - (b.x + b.width / 2));
}

export function middleFaceCheck(shot: ClipShot): FaceCheck | null {
    if (shot.faceChecks.length === 0) {
        return null;
    }
    const middle = (shot.startMs + shot.endMs) / 2;
    return shot.faceChecks.reduce((closest, check) =>
        Math.abs(check.timeMs - middle) < Math.abs(closest.timeMs - middle) ? check : closest,
    );
}

/** Where a face's centre falls in the recording's picture, e.g. "top right" or "centre". */
export function describeFacePlacement(face: BoundingBox): string {
    const third = (fraction: number, [low, mid, high]: [string, string, string]) =>
        fraction < 1 / 3 ? low : fraction > 2 / 3 ? high : mid;
    const vertical = third(face.y + face.height / 2, ['top', 'middle', 'bottom']);
    const horizontal = third(face.x + face.width / 2, ['left', 'centre', 'right']);
    return vertical === 'middle' && horizontal === 'centre'
        ? 'centre'
        : `${vertical} ${horizontal}`;
}

function describeShotSize(face: BoundingBox): string {
    if (face.height >= 0.25) {
        return 'close-up';
    }
    return face.height >= 0.12 ? 'medium shot' : 'wide shot';
}

function describeShotKind(faces: BoundingBox[]): string {
    if (faces.length === 0) {
        return 'no face: b-roll, a slide, a graphic or a cutaway';
    }
    if (faces.length === 1) {
        return `one person, ${describeShotSize(faces[0])}`;
    }
    if (faces.length <= 3) {
        return `${faces.length} people`;
    }
    return `${faces.length} faces: a group, a gallery or an audience`;
}

function formatSeconds(ms: number): string {
    return `${(ms / 1000).toFixed(1)}s`;
}

/** How the people in a long shot change between its face checks, or null when they hold still. */
export function describeFaceChanges(shot: ClipShot, regions: KeptRegion[]): string | null {
    const checks = shot.faceChecks.filter(
        (check): check is { timeMs: number; faces: BoundingBox[] } => check.faces !== null,
    );
    if (checks.length < 2) {
        return null;
    }
    const counts = checks.map((check) => check.faces.length);
    if (counts.some((count) => count !== counts[0])) {
        const timeline = checks
            .map(
                (check) =>
                    `${check.faces.length} at ${formatSeconds(toClipTimeMs(check.timeMs, regions))}`,
            )
            .join(', ');
        return `people come and go during the shot (faces: ${timeline})`;
    }
    const moved = checks[0].faces.flatMap((face, index) => {
        const centres = checks.map((check) => check.faces[index].x + check.faces[index].width / 2);
        const travel = Math.max(...centres) - Math.min(...centres);
        if (travel < FACE_MOVED_FRACTION) {
            return [];
        }
        const first = checks[0].faces[index];
        const last = checks[checks.length - 1].faces[index];
        return [
            `face ${index + 1} moves from the ${describeFacePlacement(first)} to the ${describeFacePlacement(last)}`,
        ];
    });
    return moved.length > 0 ? moved.join('; ') : null;
}

function describeFaces(faces: BoundingBox[]): string {
    return faces
        .map(
            (face, index) =>
                `${index + 1} at the ${describeFacePlacement(face)}, ${Math.round(face.height * 100)}% of the picture's height`,
        )
        .join('; ');
}

function faceCentre(face: BoundingBox): { x: number; y: number } {
    return { x: face.x + face.width / 2, y: face.y + face.height / 2 };
}

/**
 * Whether two shots frame the same people the same way, so a cut between them is a jump cut
 * within one camera angle rather than a change of angle. Shots with no face never match: two
 * pieces of b-roll can look nothing alike.
 */
export function isSameFraming(a: BoundingBox[] | null, b: BoundingBox[] | null): boolean {
    if (!a || !b || a.length === 0 || a.length !== b.length) {
        return false;
    }
    return a.every((face, index) => {
        const other = b[index];
        const centre = faceCentre(face);
        const otherCentre = faceCentre(other);
        return (
            Math.abs(centre.x - otherCentre.x) <= SAME_ANGLE_CENTRE_FRACTION &&
            Math.abs(centre.y - otherCentre.y) <= SAME_ANGLE_CENTRE_FRACTION &&
            Math.abs(face.height - other.height) <= SAME_ANGLE_HEIGHT_FRACTION
        );
    });
}

/** A, B … Z, then AA, AB …, so no two angles ever share a name. */
function angleName(index: number): string {
    const letter = String.fromCharCode(65 + (index % 26));
    return index < 26 ? letter : `${angleName(Math.floor(index / 26) - 1)}${letter}`;
}

/**
 * Name the camera angle of every shot with people in it, reusing an earlier shot's name when it
 * frames the same way, and mark the b-roll: shots with no face, and brief flashes of an angle
 * that neither neighbour shares. A speaker crop would show those through a crop framed on
 * someone else, so they play under the base scene's centre crop instead.
 */
export function assignCameraAngles(shots: ClipShot[]): void {
    let nextAngle = 0;
    const facesOf = (shot: ClipShot) => middleFaceCheck(shot)?.faces ?? null;
    shots.forEach((shot) => {
        shot.angle = '';
        shot.isBroll = facesOf(shot)?.length === 0;
    });
    const detected = shots.filter((shot) => (facesOf(shot)?.length ?? 0) > 0);
    detected.forEach((shot, index) => {
        const earlier = detected
            .slice(0, index)
            .find((other) => isSameFraming(facesOf(other), facesOf(shot)));
        shot.angle = earlier ? earlier.angle : angleName(nextAngle++);
    });
    // A failed detection says nothing about the framing, so the shot joins a neighbouring angle
    // rather than starting its own: a new angle there would change the crop at what may be a
    // jump cut.
    shots.forEach((shot, index) => {
        if (facesOf(shot) !== null) {
            return;
        }
        const neighbour =
            shots
                .slice(0, index)
                .reverse()
                .find((other) => other.angle !== '') ??
            shots.slice(index + 1).find((other) => other.angle !== '');
        shot.angle = neighbour ? neighbour.angle : angleName(nextAngle++);
    });
    shots.forEach((shot, index) => {
        const previous = shots[index - 1];
        const next = shots[index + 1];
        if (
            !shot.isBroll &&
            shot.endMs - shot.startMs < BRIEF_FLASH_MS &&
            previous?.angle !== shot.angle &&
            next?.angle !== shot.angle
        ) {
            shot.isBroll = true;
        }
    });
}

function framingKey(shot: ClipShot): string {
    return shot.isBroll ? 'b-roll' : shot.angle;
}

/** Where the framing changes: between angles, and into or out of a b-roll stretch. */
export function angleChangeTimesMs(shots: ClipShot[]): number[] {
    return shots
        .filter((shot, index) => index > 0 && framingKey(shot) !== framingKey(shots[index - 1]))
        .map((shot) => shot.startMs);
}

export interface FramingRun {
    shots: ClipShot[];
    isBroll: boolean;
    hasWords: boolean;
}

/**
 * The clip as runs of one framing: consecutive shots on the same angle (one speaker scene each,
 * so a jump cut never changes the crop) and consecutive b-roll (one stretch left to the base
 * scene's centre crop).
 */
export function planFramingRuns(shots: ClipShot[]): FramingRun[] {
    const runs: FramingRun[] = [];
    for (const shot of shots) {
        const current = runs[runs.length - 1];
        const shotHasWords = shot.tokens.some((token) => !token.isPause);
        if (current && framingKey(current.shots[0]) === framingKey(shot)) {
            current.shots.push(shot);
            current.hasWords ||= shotHasWords;
        } else {
            runs.push({ shots: [shot], isBroll: shot.isBroll, hasWords: shotHasWords });
        }
    }
    return runs;
}

/**
 * Whether two or three people in a shot are far enough apart to show each in a slot of their own,
 * from the gap between neighbouring faces measured in face widths. Null for one face or a crowd.
 */
export function describeFaceSpacing(faces: BoundingBox[]): string | null {
    if (faces.length < 2 || faces.length > 3) {
        return null;
    }
    const faceWidth = faces.reduce((sum, face) => sum + face.width, 0) / faces.length;
    const narrowestGap = Math.min(
        ...faces.slice(1).map((face, index) => face.x - (faces[index].x + faces[index].width)),
    );
    return narrowestGap >= faceWidth * FAR_APART_FACE_WIDTHS
        ? 'far apart, each with room of their own: a split or stack can show each person'
        : 'close together, sharing the picture: keep them in one crop, never split or stack them';
}

/** A one-line read on what kind of footage the clip is, to choose the reframing approach from. */
export function describeClipFootage(shots: ClipShot[]): string {
    const angles = new Set(shots.filter((shot) => !shot.isBroll).map((shot) => shot.angle));
    const broll = shots.filter((shot) => shot.isBroll).length;
    const jumpCuts = shots.filter(
        (shot, index) =>
            index > 0 && !shot.isBroll && framingKey(shot) === framingKey(shots[index - 1]),
    ).length;
    const jumpCutNote =
        jumpCuts > 0
            ? ` There ${jumpCuts === 1 ? 'is 1 jump cut' : `are ${jumpCuts} jump cuts`} within an angle, which don't change the framing.`
            : '';
    // A failed detection is unknown, not "no face": it never counts as b-roll, and it's said here
    // so the read on the footage isn't taken as complete.
    const failed = shots.filter((shot) => (middleFaceCheck(shot)?.faces ?? null) === null);
    const failedNote =
        failed.length > 0
            ? ` Face detection failed on ${failed.length} ${failed.length === 1 ? 'shot' : 'shots'} (${failed.length === 1 ? 'shot' : 'shots'} ${failed.map((shot) => shot.number).join(', ')}); check ${failed.length === 1 ? 'it' : 'them'} on the contact sheet.`
            : '';
    if (angles.size === 0) {
        return `No faces anywhere: all b-roll, slides or graphics.${failedNote}`;
    }
    if (angles.size === 1 && broll === 0) {
        const faces = middleFaceCheck(shots[0])?.faces;
        const faceCount = faces
            ? `${faces.length} ${faces.length === 1 ? 'face' : 'faces'}`
            : 'faces not detected';
        return `One camera angle throughout (a fixed camera), ${faceCount}.${jumpCutNote}${failedNote}`;
    }
    const angleCount = `${angles.size} camera ${angles.size === 1 ? 'angle' : 'angles'} of people`;
    if (broll === 0) {
        return `Edited footage: ${angleCount}.${jumpCutNote}${failedNote}`;
    }
    return `Produced footage: ${angleCount}, and ${broll} of the ${shots.length} shots are b-roll (no face, or a brief flash between other angles).${jumpCutNote}${failedNote}`;
}

function previewWords(tokens: InterleavedWord[]): string {
    const words = tokens.map(interleavedTokenToString);
    if (words.length <= WORD_PREVIEW_EDGE * 2 + 2) {
        return words.join(' ');
    }
    return `${words.slice(0, WORD_PREVIEW_EDGE).join(' ')} … ${words.slice(-WORD_PREVIEW_EDGE).join(' ')}`;
}

function describeShotNumbers(shots: ClipShot[]): string {
    const first = shots[0].number;
    const last = shots[shots.length - 1].number;
    return first === last ? `shot ${first}` : `shots ${first}–${last}`;
}

export function formatShotTable(shots: ClipShot[], regions: KeptRegion[]): string {
    const lines = [
        `Shot table: ${shots.length} ${shots.length === 1 ? 'shot' : 'shots'} in the clip. ${describeClipFootage(shots)}`,
        "Faces are numbered left to right across the recording's picture, the way frame_speaker numbers them, as found at the middle of the shot. Shots with the same angle letter frame the same people the same way. The contact sheet below shows the same frames with each face boxed and numbered.",
        '',
    ];
    shots.forEach((shot, index) => {
        const clipStart = toClipTimeMs(shot.startMs, regions);
        const clipEnd = toClipTimeMs(shot.endMs, regions);
        const duration = clipEnd - clipStart;
        const middle = middleFaceCheck(shot);
        const kind =
            middle?.faces === null || middle === null
                ? 'face detection failed'
                : describeShotKind(middle.faces);
        const brief = duration < BRIEF_SHOT_MS ? ' · brief' : '';
        const previous = shots[index - 1];
        const framing = shot.isBroll
            ? 'b-roll'
            : `angle ${shot.angle}${previous && !previous.isBroll && previous.angle === shot.angle ? ` (jump cut from shot ${previous.number})` : ''}`;
        lines.push(
            `Shot ${shot.number} · ${framing} · ${formatSeconds(clipStart)}–${formatSeconds(clipEnd)} (${formatSeconds(duration)})${brief} · ${kind}`,
        );
        if (middle?.faces && middle.faces.length > 0) {
            lines.push(`  faces: ${describeFaces(middle.faces)}`);
            const spacing = describeFaceSpacing(middle.faces);
            if (spacing) {
                lines.push(`  spacing: ${spacing}`);
            }
        }
        const changes = describeFaceChanges(shot, regions);
        if (changes) {
            lines.push(`  ${changes}`);
        }
        const words = shot.tokens.filter((token) => !token.isPause);
        lines.push(
            words.length > 0
                ? `  words: ${previewWords(shot.tokens)}`
                : '  words: none (it falls between words, so it plays under a neighbouring scene)',
        );
    });

    const runs = planFramingRuns(shots);
    lines.push(
        '',
        'Speaker scenes to add: one per run of the same camera angle, so the crop changes only when the angle does. Each lists the exact words to cover, ready to copy.',
    );
    let sceneNumber = 0;
    for (const run of runs) {
        if (run.isBroll) {
            continue;
        }
        if (!run.hasWords) {
            lines.push(
                `(${describeShotNumbers(run.shots)} · angle ${run.shots[0].angle}: no words, so it plays under a neighbouring scene)`,
            );
            continue;
        }
        sceneNumber++;
        lines.push(
            `Scene ${sceneNumber} · ${describeShotNumbers(run.shots)} · angle ${run.shots[0].angle}`,
            `  words: ${run.shots
                .flatMap((shot) => shot.tokens)
                .map(interleavedTokenToString)
                .join(' ')}`,
        );
    }
    const brollRuns = runs.filter((run) => run.isBroll);
    if (brollRuns.length > 0) {
        lines.push(
            '',
            "B-roll stretches: add no scene; they play under the base scene's centre crop.",
            ...brollRuns.map((run) => {
                const tokens = run.shots.flatMap((shot) => shot.tokens);
                return `- ${describeShotNumbers(run.shots)}${tokens.some((token) => !token.isPause) ? `: ${previewWords(tokens)}` : ' (no words)'}`;
            }),
        );
    }
    return lines.join('\n');
}

/**
 * The angle changes a scene plays across: ones with some of the scene's words before them and
 * some after. Deciding by the scene's words, the way the shot table assigns them, means a pause
 * at the end of a scene running past a cut doesn't count.
 */
export function findCutsBetweenWords(cutTimesMs: number[], wordStartTimesMs: number[]): number[] {
    if (wordStartTimesMs.length === 0) {
        return [];
    }
    const first = Math.min(...wordStartTimesMs);
    const last = Math.max(...wordStartTimesMs);
    return cutTimesMs.filter((cut) => cut > first && cut <= last);
}

/** The part of the recording a slot shows, as fractions of the recording's picture. */
export function cropWindow(
    asset: TimeAwareMediaAsset,
    recording: { width: number; height: number },
): { left: number; right: number; top: number; bottom: number } {
    const { width: slotWidth, height: slotHeight } = asset.mediaTargetResolution;
    const sourceRatio = recording.width / recording.height;
    const isHeightBound = slotWidth / slotHeight < sourceRatio;
    const scaledWidth = (isHeightBound ? slotHeight * sourceRatio : slotWidth) * asset.scaleX;
    const scaledHeight = (isHeightBound ? slotHeight : slotWidth / sourceRatio) * asset.scaleY;
    return {
        left: 0.5 + (asset.xTranslation * slotWidth - slotWidth / 2) / scaledWidth,
        right: 0.5 + (asset.xTranslation * slotWidth + slotWidth / 2) / scaledWidth,
        top: 0.5 + (asset.yTranslation * slotHeight - slotHeight / 2) / scaledHeight,
        bottom: 0.5 + (asset.yTranslation * slotHeight + slotHeight / 2) / scaledHeight,
    };
}
